import { useState, useRef, useEffect } from 'react';
import { Box, Flex, Image } from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { CloseIcon, UploadIcon, PlusIcon } from 'Asset/icon';
import { Condition, Iteration } from 'Component/others';
import InputTextAreaComponent from 'UiKit/input-textarea';
import ButtonSolid from 'UiKit/button/buttonSolid';
import Drawer from 'UiKit/drawer';
import { TextBold, TextMedium, TextRegular, TextSemiBold } from 'UiKit/typography';

import usePostUpdateGiveawayStock from 'Domain/productivity-host/usePostUpdateGiveawayStock';
import usePostHostUserImage from 'Domain/productivity-host/usePostHostUserImage';
import useGetDraftGiveawaySession from 'Domain/productivity-host/useGetDraftGiveawaySession';

import styles from './styles.module.css';

const DrawerUpdateGiveaway = ({ data, isOpen, onClose }) => {
  const { t } = useTranslation('host-stream');
  const queryClient = useQueryClient();
  const { productName, title, imagePath, allocatedStock, usedStock, sessionId } = data ?? {};

  const [winnerInfo, setWinnerInfo] = useState('');
  const [usedStockHostSessionCount, setUsedStockHostSessionCount] = useState(0);
  const [photos, setPhotos] = useState([]);
  const [photoError, setPhotoError] = useState(false);
  const fileInputRef = useRef(null);
  const initialUsedStockRef = useRef(0);

  const { mutate: updateGiveawayStock, isPending } = usePostUpdateGiveawayStock();
  const { mutate: uploadUserImage, isPending: isUploading } = usePostHostUserImage();
  const { data: draftGiveawaySession } = useGetDraftGiveawaySession({ isOpen, sessionId });

  const baseRemaining = (allocatedStock ?? 0) - (usedStock ?? 0);
  const maxSessionCount = initialUsedStockRef.current + baseRemaining;
  const remainingStock = baseRemaining + initialUsedStockRef.current - usedStockHostSessionCount;
  const isIncrementDisabled = usedStockHostSessionCount >= maxSessionCount;
  const isDecrementDisabled = usedStockHostSessionCount <= 0;

  const handleDecrement = () => setUsedStockHostSessionCount(Math.max(0, usedStockHostSessionCount - 1));
  const handleIncrement = () => setUsedStockHostSessionCount(Math.min(maxSessionCount, usedStockHostSessionCount + 1));

  const handleRemovePhoto = (index) => {
    setPhotos((prev) => prev.filter((_, i) => i !== index));
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    uploadUserImage(
      { file },
      {
        onSuccess: (result) => {
          setPhotos((prev) => [...prev, { id: result.key, preview: result.imagePath, path: result.key }]);
          setPhotoError(false);
        },
      },
    );

    e.target.value = '';
  };

  const handleSave = () => {
    if (usedStockHostSessionCount > 0 && photos.length === 0) {
      setPhotoError(true);
      return;
    }
    const confirmedPaths = photos.filter((p) => p.path).map((p) => p.path);
    updateGiveawayStock(
      {
        briefGiveawaySessionId: sessionId,
        usedStock: usedStockHostSessionCount,
        ...(winnerInfo && { note: winnerInfo }),
        ...(confirmedPaths.length > 0 && { imagePath: confirmedPaths }),
      },
      { onSuccess: onClose },
    );
  };

  useEffect(() => {
    if (usedStockHostSessionCount === 0) setPhotoError(false);
  }, [usedStockHostSessionCount]);

  useEffect(() => {
    if (!draftGiveawaySession) return;
    const draftUsedStock = draftGiveawaySession.usedStock ?? 0;
    setWinnerInfo(draftGiveawaySession.note ?? '');
    setUsedStockHostSessionCount(draftUsedStock);
    initialUsedStockRef.current = draftUsedStock;
    setPhotos(
      (draftGiveawaySession.images ?? []).map((img) => ({
        id: img.imageKey,
        preview: img.imagePath,
        path: img.imageKey,
      })),
    );
  }, [draftGiveawaySession]);

  useEffect(() => {
    queryClient.invalidateQueries({ queryKey: ['giveaway-brief'] });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Drawer className={styles['drawer-update-giveaway__drawer']} height="auto" isOpen={isOpen} onClose={onClose}>
      <Box className={styles['drawer-update-giveaway__section--head']}>
        <TextBold className={styles['drawer-update-giveaway__text--title']}>{t('update_giveaway.title')}</TextBold>
        <CloseIcon size="18px" onClick={onClose} />
      </Box>

      <Box className={styles['drawer-update-giveaway__section--content']}>
        <Box className={styles['drawer-update-giveaway__product--info']}>
          <Box className={styles['drawer-update-giveaway__product--image-wrapper']}>
            <Condition statement={imagePath}>
              <Image src={imagePath} className={styles['drawer-update-giveaway__product--image']} />
            </Condition>
          </Box>
          <Box>
            <TextSemiBold className={styles['drawer-update-giveaway__product--name']}>{productName}</TextSemiBold>
            <TextRegular className={styles['drawer-update-giveaway__product--title']}>{title}</TextRegular>
          </Box>
        </Box>

        <Box className={styles['drawer-update-giveaway__divider']} />

        <Box className={styles['drawer-update-giveaway__field--group']}>
          <Flex align="center" gap="4px">
            <TextSemiBold className={styles['drawer-update-giveaway__field--label']}>{t('update_giveaway.winner_info')}</TextSemiBold>
            <TextRegular className={styles['drawer-update-giveaway__field--label-optional']}>{t('update_giveaway.optional')}</TextRegular>
          </Flex>
          <InputTextAreaComponent
            placeholder={t('update_giveaway.winner_info_placeholder')}
            value={winnerInfo}
            onChange={(e) => setWinnerInfo(e.target.value)}
            maxLength={500}
          />
        </Box>

        <Box className={styles['drawer-update-giveaway__field--group']}>
          <Flex align="center" gap="4px">
            <TextSemiBold className={styles['drawer-update-giveaway__field--label']}>
              {t('update_giveaway.proof_of_photo')}{' '}
              {usedStockHostSessionCount > 0 && <span className={styles['drawer-update-giveaway__field--label-required']}>*</span>}
            </TextSemiBold>
          </Flex>
          <input ref={fileInputRef} type="file" accept="image/*" hidden onChange={handlePhotoChange} />
          <Flex gap="8px" flexWrap="wrap">
            <Box className={styles['drawer-update-giveaway__upload--box']} onClick={() => !isUploading && fileInputRef.current?.click()}>
              <UploadIcon size="24px" color="var(--color-neutral50)" />
            </Box>
            <Iteration
              items={photos}
              render={(photo, index) => (
                <Box key={photo.id} className={styles['drawer-update-giveaway__upload--preview-wrapper']}>
                  <Box className={styles['drawer-update-giveaway__upload--box']}>
                    <Image src={photo.preview} className={styles['drawer-update-giveaway__upload--preview']} />
                  </Box>
                  <Box className={styles['drawer-update-giveaway__upload--delete']} onClick={() => handleRemovePhoto(index)}>
                    <CloseIcon size="10px" color="var(--color-semantic-white)" />
                  </Box>
                </Box>
              )}
            />
          </Flex>
          <Condition statement={photoError}>
            <TextRegular className={styles['drawer-update-giveaway__field--error']}>Foto bukti wajib diisi</TextRegular>
          </Condition>
        </Box>

        <Box className={styles['drawer-update-giveaway__divider']} />

        <Box className={styles['drawer-update-giveaway__stock--display']}>
          <TextRegular className={styles['drawer-update-giveaway__stock--label']}>{t('update_giveaway.stock')}</TextRegular>
          <TextBold className={styles['drawer-update-giveaway__stock--value']}>{remainingStock}</TextBold>
        </Box>

        <Flex className={styles['drawer-update-giveaway__stock--counter-row']} align="center" justify="space-between">
          <TextSemiBold className={styles['drawer-update-giveaway__field--label']}>{t('update_giveaway.stock_used')}</TextSemiBold>
          <Flex align="center" gap="8px">
            <Box
              className={`${styles['drawer-update-giveaway__counter--button']} ${isDecrementDisabled ? styles['drawer-update-giveaway__counter--button-disabled'] : ''}`}
              onClick={handleDecrement}
            >
              <TextMedium className={styles['drawer-update-giveaway__counter--icon']}>-</TextMedium>
            </Box>
            <Box className={styles['drawer-update-giveaway__counter--value-box']}>
              <TextRegular className={styles['drawer-update-giveaway__counter--value']}>{usedStockHostSessionCount}</TextRegular>
            </Box>
            <Box
              className={`${styles['drawer-update-giveaway__counter--button']} ${isIncrementDisabled ? styles['drawer-update-giveaway__counter--button-disabled'] : ''}`}
              onClick={handleIncrement}
            >
              <PlusIcon size="16px" color="var(--color-primary70)" />
            </Box>
          </Flex>
        </Flex>
      </Box>

      <Box className={styles['drawer-update-giveaway__section--footer']}>
        <ButtonSolid width="100%" height="48px" onClick={handleSave} isDisabled={isPending || isUploading} isLoading={isPending}>
          {t('common:save')}
        </ButtonSolid>
      </Box>
    </Drawer>
  );
};

export default DrawerUpdateGiveaway;
